/**
 * Controlled input form field with clear button.
 * @author @CorradoSurmanowicz
 */
import { Control, FieldErrors, Path } from "react-hook-form";
import { FormFieldError, FormFieldLayout } from "./FormFieldLayouts";
import styles from "./formfields.module.css";
import { CSSProperties } from "react";
import { StringIndexedDict } from "../../api/GenericTypes";
import { getError } from "./GenericVirtualizedSelectFormField/GenericVirtualizedEntitySelectFormFieldControllerWrapper";
import { GenericControllerProvider, GenericControllerProviderProps } from "../forms/common/GenericControllerProvider";
import { LucideIcon } from "../icon/LucideIcon";

export interface InputFormFieldControlledProps<Entity extends StringIndexedDict> {
  id: Path<Entity>;
  label: string;
  errors?: FieldErrors<Entity>;
  control: Control<Entity, any, Entity>;
  required?: boolean;
  autoFocus?: boolean;
  readOnly?: boolean;
  type?: "text" | "textarea" | "date" | "datetime-local" | "number" | "password" | "radio";
  horizontal?: boolean;
  values?: any[];
  placeholder?: any;
  style?: CSSProperties;
  wrapperContainerstyle?: CSSProperties;
  defaultValue?: string | number;
  buttons?: React.ReactNode;
  uncontained?: boolean;
  autoComplete?: string;
  disabled?: boolean;
  children?: React.ReactNode;
  min?: number;
  max?: number;
  controllerFieldOverride?: GenericControllerProviderProps<Entity>["controllerFieldOverride"];
  inputProps?: React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>;
}

export const InputFormFieldControlled = <Entity extends StringIndexedDict>({
  id,
  label,
  errors,
  control,
  required,
  autoFocus,
  readOnly,
  type = "text",
  horizontal = true,
  placeholder = "Insert text...",
  style,
  wrapperContainerstyle,
  defaultValue,
  buttons,
  uncontained = false,
  autoComplete = "off",
  disabled = false,
  children,
  min,
  max,
  controllerFieldOverride,
  inputProps,
}: InputFormFieldControlledProps<Entity>) => {
  // Helper for clear button
  const showClear = (value: any) =>
    !readOnly &&
    !disabled &&
    typeof value === "string" &&
    value.length > 0 &&
    type !== "date" &&
    type !== "datetime-local" &&
    type !== "number" &&
    type !== "radio" &&
    type !== "password";

  return (
    <GenericControllerProvider<Entity>
      id={id}
      control={control}
      defaultValue={defaultValue}
      controllerFieldOverride={controllerFieldOverride}
    >
      {({ value, onChange, onBlur }, fieldState, { errors }) => {
        const error = errors ? getError<Entity>(id, errors) : undefined;
        const inputField = (
          <div className="flex row-nowrap align-center" style={{ position: "relative", width: "100%", height: "100%" }}>
            <input
              className={`${error ? "border-danger form-control" : "form-control"}${required ? " required" : ""}`}
              value={value === null ? "" : value}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              type={type}
              autoFocus={autoFocus}
              readOnly={readOnly}
              placeholder={placeholder}
              style={{
                ...style,
                ...(readOnly && { cursor: "not-allowed" }),
                height: "100%",
                paddingRight: showClear(value)
                  ? buttons
                    ? 30 // enough space for clear + buttons
                    : 30 // enough space for clear button only
                  : buttons
                  ? 30 // enough space for buttons only
                  : undefined,
              }}
              autoComplete={autoComplete}
              disabled={disabled}
              min={min}
              max={max}
              {...inputProps}
            />
            {showClear(value) && (
              <button
                type="button"
                aria-label="Clear"
                onClick={() => onChange("")}
                className={styles.clearButton}
                style={{
                  position: "absolute",
                  right: buttons ? 30 : 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  fontSize: "1.2em",
                  padding: 0,
                  zIndex: 2,
                  lineHeight: 1,
                }}
                tabIndex={-1}
              >
                <LucideIcon name="x" />
              </button>
            )}
            {buttons ? <div className={styles.buttons}>{buttons}</div> : null}
            {children}
          </div>
        );

        return (
          <>
            {uncontained ? (
              <>
                <div className={`flex`} style={{ width: "100%", height: "100%", ...wrapperContainerstyle }}>
                  {inputField}
                </div>
                <FormFieldError id={id} errors={errors} />
              </>
            ) : (
              <FormFieldLayout
                id={id}
                label={label}
                required={required}
                horizontal={horizontal}
                hasLabel={label !== ""}
              >
                <div className={styles.container} style={wrapperContainerstyle}>
                  {inputField}
                </div>
                <FormFieldError id={id} errors={errors} />
              </FormFieldLayout>
            )}
          </>
        );
      }}
    </GenericControllerProvider>
  );
};
